import * as stylex from "@stylexjs/stylex";
import { useState, type ReactNode } from "react";
import type { CatalogMediaItem } from "./media-types";
import { styles as mediaStyles } from "./media.styles";
import { colors } from "@softmax/design-system/tokens.stylex";

export function StageContent({
  item,
  board,
  replayEmbed,
  onError,
  containOdd,
}: {
  readonly item: CatalogMediaItem;
  readonly board: ReactNode;
  readonly replayEmbed: ReactNode;
  readonly onError: () => void;
  readonly containOdd: boolean;
}) {
  if (item.kind === "board") {
    return (
      <div {...stylex.props(styles.board)}>
        <div {...stylex.props(styles.boardScale)}>{board}</div>
      </div>
    );
  }

  if (item.kind === "replay") return replayEmbed;

  if (item.kind === "highlight" && !item.src.endsWith(".webp")) {
    return (
      <video
        src={item.src}
        poster={item.poster ?? undefined}
        aria-label={item.alt}
        autoPlay
        muted
        loop
        playsInline
        onError={onError}
        {...stylex.props(mediaStyles.image)}
      />
    );
  }

  return <StageImage item={item} onError={onError} containOdd={containOdd} />;
}

/** Landscape frames cover; an odd aspect (portrait, near-square) is contained. */
function StageImage({
  item,
  onError,
  containOdd,
}: {
  readonly item: CatalogMediaItem;
  readonly onError: () => void;
  readonly containOdd: boolean;
}) {
  const [odd, setOdd] = useState(false);
  return (
    <img
      src={item.src}
      alt={item.alt}
      onError={onError}
      onLoad={(event) => {
        const { naturalWidth, naturalHeight } = event.currentTarget;
        setOdd(naturalHeight > 0 && naturalWidth / naturalHeight < 1.3);
      }}
      {...stylex.props(mediaStyles.image, containOdd && odd && styles.contain)}
    />
  );
}

const styles = stylex.create({
  board: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "100%",
    height: "100%",
    backgroundColor: colors.surfaceAlt,
    containerType: "size",
  },
  boardScale: {
    flexShrink: 0,
    transformOrigin: "center",
    transform: "scale(calc(min(100cqw, 100cqh) / 396px))",
  },
  contain: { objectFit: "contain" },
});
